import { ComponentProps, FC } from 'react';

import { Chip, CircularProgress, SvgIconProps } from '@mui/material';

import capitalize from 'lodash/capitalize';

import AlertCircleIcon from '@/components/Icons/AlertCircleIcon';
import CheckIcon from '@/components/Icons/CheckIcon';
import StopCircleIcon from '@/components/Icons/StopCircleIcon';
import { IconWrapper } from '@/components/StatusBadge/styled';
import { TrainingJobStatus } from '@/graphql';
import { assertNever } from '@/utils/assertNever';
import moment from 'moment';

import { TrainingJobFragment } from './__generated__/fragments.generated';

enum TrainingJobStatusGroup {
  Completed = 'COMPLETED',
  InProgress = 'IN_PROGRESS',
  Failed = 'FAILED',
  Stopped = 'STOPPED',
}

const MAP_STATUS_GROUP_TO_COLOR: Record<
  TrainingJobStatusGroup,
  ComponentProps<typeof Chip>['color']
> = {
  [TrainingJobStatusGroup.Completed]: 'success',
  [TrainingJobStatusGroup.InProgress]: 'info',
  [TrainingJobStatusGroup.Failed]: 'error',
  [TrainingJobStatusGroup.Stopped]: 'secondary',
};

const MAP_STATUS_GROUP_TO_ICON: Record<TrainingJobStatusGroup, FC<SvgIconProps>> = {
  [TrainingJobStatusGroup.Completed]: CheckIcon,
  [TrainingJobStatusGroup.InProgress]: () => (
    <IconWrapper>
      <CircularProgress size={10} />
    </IconWrapper>
  ),
  [TrainingJobStatusGroup.Failed]: AlertCircleIcon,
  [TrainingJobStatusGroup.Stopped]: StopCircleIcon,
};

const ORDERED_STATUS_GROUPS = [
  TrainingJobStatusGroup.Completed,
  TrainingJobStatusGroup.InProgress,
  TrainingJobStatusGroup.Failed,
  TrainingJobStatusGroup.Stopped,
];

function mapTrainingJobStatusToGroup(status: TrainingJobStatus) {
  switch (status) {
    case TrainingJobStatus.TrainingJobCompleted:
      return TrainingJobStatusGroup.Completed;
    case TrainingJobStatus.TrainingJobCreated:
    case TrainingJobStatus.TrainingJobQueued:
    case TrainingJobStatus.TrainingJobDeploying:
    case TrainingJobStatus.TrainingJobRunning:
      return TrainingJobStatusGroup.InProgress;
    case TrainingJobStatus.TrainingJobDeployFailed:
    case TrainingJobStatus.TrainingJobFailed:
      return TrainingJobStatusGroup.Failed;
    case TrainingJobStatus.TrainingJobStopped:
      return TrainingJobStatusGroup.Stopped;
    default:
      assertNever(status);
  }
}

function mapTrainingJobStatusToChip(status: TrainingJobStatus, onlyWithIcon: boolean = false) {
  const statusGroup = mapTrainingJobStatusToGroup(status);
  const Icon = MAP_STATUS_GROUP_TO_ICON[statusGroup];

  if (onlyWithIcon) {
    return <Icon color={MAP_STATUS_GROUP_TO_COLOR[statusGroup] as SvgIconProps['color']} />;
  }

  return (
    <Chip
      size="small"
      color={MAP_STATUS_GROUP_TO_COLOR[statusGroup]}
      icon={<Icon />}
      key={statusGroup}
      label={capitalize(status.replace(/^training_job_/i, '').replace(/_/g, ' '))}
    />
  );
}

function mapCheckpointTypeToTitle(type: string) {
  switch (type) {
    case 'lora':
      return 'LoRA';
    case 'full':
      return 'Full';
    case 'whisper':
      return 'Whisper';
    case 'distributed_full':
      return 'Distributed';
    case 'verl_fsdp':
      return 'VERL FSDP';
    case 'verl_megatron_checkpoint':
      return 'VERL Megatron';
    case 'verl_checkpoint':
      return 'VERL Checkpoint';
    default:
      return capitalize(type);
  }
}

function mapTrainingJobStatusGroupToTitle(status: TrainingJobStatus, name: string) {
  const statusGroup = mapTrainingJobStatusToGroup(status);

  switch (statusGroup) {
    case TrainingJobStatusGroup.InProgress:
      return `${name} is currently running`;
    case TrainingJobStatusGroup.Completed:
      return `${name} completed`;
    case TrainingJobStatusGroup.Failed:
      return `${name} failed`;
    case TrainingJobStatusGroup.Stopped:
      return `${name} stopped`;
    default:
      return name;
  }
}

function isTrainingJobStatusInProgress(status: TrainingJobStatus) {
  return mapTrainingJobStatusToGroup(status) === TrainingJobStatusGroup.InProgress;
}

function getTrainingJobElapsedTime(trainingJob: TrainingJobFragment) {
  const inProgress = isTrainingJobStatusInProgress(trainingJob.status.value);

  const elapsedTime = moment.duration(trainingJob.elapsedTimeSeconds, 'seconds').humanize();

  if (inProgress) {
    return `Running for ${elapsedTime}`;
  }

  return ` Ran for ${elapsedTime}`;
}

export {
  TrainingJobStatusGroup,
  mapTrainingJobStatusToGroup,
  mapTrainingJobStatusToChip,
  isTrainingJobStatusInProgress,
  getTrainingJobElapsedTime,
  MAP_STATUS_GROUP_TO_ICON,
  MAP_STATUS_GROUP_TO_COLOR,
  ORDERED_STATUS_GROUPS,
  mapTrainingJobStatusGroupToTitle,
  mapCheckpointTypeToTitle,
};
